import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Button, Card, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { appPath } from '../../../routes/paths';
import { useAuth } from '../hooks/useAuth';
import { resendOtpApi } from '../api/auth';
import { ApiError } from '../../../lib/api/error';
const { Text } = Typography;

interface LoginPageProps {
  /** Short helper shown below the card title. */
  lead?: string;
}

const RESEND_COOLDOWN = 30;

function resolvePostLoginPath(state: unknown): string {
  const from =
    state && typeof state === 'object' && 'from' in state && typeof state.from === 'string'
      ? state.from
      : null;
  if (from && from.startsWith('/app')) {
    return from;
  }
  return appPath();
}

interface FormValues {
  email: string;
  password: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ lead }) => {
  const [loading, setLoading] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [loginForm] = Form.useForm<FormValues>();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOtp } = useAuth();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const startCooldown = useCallback(() => {
    clearTimer();
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimer]);

  const onFinish = async (values: FormValues) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result.success && 'otpRequired' in result) {
        setOtpSessionId(result.otpSessionId);
        startCooldown();
        toast.info('A verification code has been sent to your email');
      } else if (result.success) {
        navigate(resolvePostLoginPath(location.state), { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error(ApiError.fromAxiosError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const onOtpFinish = async (values: { code: string }) => {
    if (!otpSessionId) return;
    setLoading(true);
    try {
      const result = await verifyOtp(otpSessionId, values.code);
      if (result.success) {
        navigate(resolvePostLoginPath(location.state), { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error(ApiError.fromAxiosError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!otpSessionId || cooldown > 0) return;
    setResending(true);
    try {
      const result = await resendOtpApi(otpSessionId);
      if (result.success) {
        setOtpSessionId(result.otpSessionId);
        startCooldown();
        toast.success('A new verification code has been sent');
      } else {
        toast.error(result.error);
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    clearTimer();
    setCooldown(0);
    setOtpSessionId(null);
    loginForm.resetFields();
  };

  const formBody = otpSessionId ? (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Enter the 6-digit verification code sent to your email.
      </Text>
      <Form layout="vertical" onFinish={onOtpFinish} autoComplete="off">
        <Form.Item
          name="code"
          rules={[
            { required: true, message: 'Please enter the verification code' },
            { len: 6, message: 'Code must be 6 digits' },
          ]}
        >
          <Input.OTP length={6} size="large" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Verify
          </Button>
        </Form.Item>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          {cooldown > 0 ? (
            <Text type="secondary">Resend code in {cooldown}s</Text>
          ) : (
            <Button
              type="link"
              onClick={handleResend}
              loading={resending}
              style={{ padding: 0 }}
            >
              Resend code
            </Button>
          )}
        </div>
        <Button type="link" block onClick={handleBackToLogin}>
          Back to login
        </Button>
      </Form>
    </>
  ) : (
    <Form
      form={loginForm}
      name="login"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
    >
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' },
        ]}
      >
        <Input placeholder="you@example.com" />
      </Form.Item>
      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: 'Please enter your password' }]}
      >
        <Input.Password placeholder="Password" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block>
          Sign in
        </Button>
      </Form.Item>
      <div style={{ textAlign: 'center' }}>
        <Link to="/forgot-password">Forgot password?</Link>
      </div>
    </Form>
  );

  return (
    <Card
      className="cp-marketing-auth-card cp-auth-panel__card"
      title={otpSessionId ? 'Verification' : 'Sign in'}
      style={{ width: '100%' }}
    >
      {lead && !otpSessionId ? (
        <Text type="secondary" className="cp-auth-panel__card-lead">
          {lead}
        </Text>
      ) : null}
      <div className="cp-auth-panel__form">{formBody}</div>
    </Card>
  );
};

export default LoginPage;
